'use client';

import React, { useState, useRef, useEffect } from 'react';
import { MessageSquare, X, Send, Bot, Sparkles, MessageCircle, Check, Smile, Clock, Info, Package, TruckIcon, CreditCard, ThumbsUp, Star, Award, Badge, Zap } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

// Define our own constants instead of importing them
const initialMessages = [
  {
    id: '1',
    text: "Hello! I'm your packaging assistant. How can I help you today?",
    sender: 'ai',
    timestamp: new Date(),
    category: 'greeting'
  },
];

// Suggested questions to help users start the conversation
const suggestedQuestions = [
  "What packaging solutions do you offer?",
  "Do you create custom designs?",
  "What are your delivery timeframes?",
  "Can I request material samples?",
  "Do you offer eco-friendly options?"
];

type Message = {
  id: string;
  text: string;
  sender: 'user' | 'ai';
  timestamp: Date;
  category?: ResponseCategory;
};

// Categories for AI responses
type ResponseCategory = 'greeting' | 'product' | 'pricing' | 'shipping' | 'payment' | 'returns' | 'general';

// Define shared interface for the LiveChat adapter 
export interface LiveChatProps {
  mode?: 'simple' | 'comprehensive';
  companyName?: string;
  assistantName?: string;
  logo?: React.ReactNode;
  position?: 'bottom-right' | 'bottom-left' | 'top-right' | 'top-left';
  initialMessage?: string;
  primaryColor?: string;
  secondaryColor?: string;
}

// Categorized AI responses for more context-aware interactions
const aiResponsesByCategory: Record<ResponseCategory, string[]> = {
  greeting: [
    "Hello! How can I assist with your packaging needs today?",
    "Welcome to our Premium Packaging service! How may I help you?",
    "Hi there! I'm here to help with all your packaging requirements.",
    "Thanks for reaching out! What can I help you with today?"
  ],
  product: [
    "Our premium packaging solutions include fridge bottles, makhana jars, and customized packaging options with eco-friendly materials.",
    "We specialize in food-grade packaging including BPA-free bottles, air-tight jars, and custom packaging solutions for various industries.",
    "All our packaging materials are BPA-free, food grade certified, and available in various sizes and designs.",
    "Our product line features premium glass and food-grade plastic options with customizable branding opportunities."
  ],
  pricing: [
    "We offer competitive pricing with bulk discounts starting at orders over 500 units. Would you like a detailed quote?",
    "Our pricing depends on the product, quantity, and customization level. I'd be happy to arrange for a personalized quote for your needs.",
    "We have flexible pricing options starting from ₹15 per unit, with significant volume discounts available.",
    "Sample kits are available for ₹500, which is refundable when you place your first order with us."
  ],
  shipping: [
    "Standard delivery takes 5-7 business days across India. Express options are available for urgent requirements.",
    "We ship internationally to over 30 countries with delivery timeframes of 10-14 business days.",
    "Orders over ₹10,000 qualify for free shipping within India. International shipping rates depend on location.",
    "We provide real-time tracking for all shipments and guarantee safe delivery of your packaging products."
  ],
  payment: [
    "We accept all major credit cards, UPI payments, net banking, and offer 30-day payment terms for registered business clients.",
    "Secure payment options include bank transfers, credit cards, and digital wallets like PayTM and Google Pay.",
    "For large orders, we offer flexible payment plans including advance and post-delivery payment options.",
    "All transactions are secure and we provide detailed invoices for your accounting purposes."
  ],
  returns: [
    "We offer a 100% quality guarantee. If you're not satisfied with your order, we'll replace it or provide a refund within 7 days.",
    "Our products undergo rigorous quality checks, but in case of any defects, please notify us within 48 hours of receiving your order.",
    "For custom orders, we send design proofs for approval before production to ensure your complete satisfaction.",
    "We stand behind our products with a comprehensive quality assurance program and responsive customer service."
  ],
  general: [
    "Our minimum order quantity is 100 pieces per design, with more favorable pricing for larger quantities.",
    "Yes, we can definitely create custom branding on all packaging products including embossing, debossing, and full-color printing.",
    "Our design team can work with your existing brand guidelines or help you create a unique packaging identity.",
    "We use sustainable materials and eco-friendly production processes to minimize environmental impact."
  ]
};

// Quick replies to follow up user queries
const followUpRepliesByCategory: Record<ResponseCategory, string[]> = {
  product: [
    "Would you like to see some samples?",
    "Can I provide more details on any specific product?",
    "What type of product will you be packaging?"
  ],
  pricing: [
    "Shall I prepare a quote for you?",
    "How many units are you considering?",
    "Would you like to discuss bulk discounts?"
  ],
  shipping: [
    "Do you need international shipping?",
    "When do you need the delivery by?",
    "Would you like information about our express options?"
  ],
  payment: [
    "What payment method do you prefer?",
    "Would you like details on our payment terms?",
    "Are you interested in our business account options?"
  ],
  returns: [
    "Have you had any issues with packaging before?",
    "Do you need information about our quality control process?",
    "Would you like to speak with a customer service specialist?"
  ],
  general: [
    "Can I help with anything else?",
    "Would you like more information?",
    "Do you have any other questions?"
  ],
  greeting: [
    "What brings you to our packaging service today?",
    "Are you looking for any specific packaging solution?",
    "How can I make your packaging experience better?"
  ]
};

const LiveChat: React.FC<LiveChatProps> = ({ 
  mode = 'comprehensive',
  companyName = 'PAVISHA',
  assistantName = 'Package Assistant',
  logo,
  position = 'bottom-right',
  initialMessage = "Hello! I'm your packaging assistant. How can I help you today?",
  primaryColor = 'red',
  secondaryColor = 'gold'
}) => {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [messages, setMessages] = useState<Message[]>([
    {
      id: '1',
      text: initialMessage,
      sender: 'ai',
      timestamp: new Date(),
      category: 'greeting' as ResponseCategory
    }
  ]);
  const [inputValue, setInputValue] = useState<string>('');
  const [isTyping, setIsTyping] = useState<boolean>(false);
  const [showPulse, setShowPulse] = useState<boolean>(false);
  const [userName, setUserName] = useState<string | null>(null);
  const [quickReply, setQuickReply] = useState<string[]>([]);
  
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Make sure we have default values for data that gets mapped over
  const messagesToRender = messages || [];
  const quickReplies = quickReply || [];
  const questionsToShow = suggestedQuestions || [];

  // Pulse animation effect with higher contrast
  useEffect(() => {
    const pulseTiming = setInterval(() => {
      setShowPulse(true);
      setTimeout(() => setShowPulse(false), 2000);
    }, 8000);
    
    return () => clearInterval(pulseTiming);
  }, []);

  // Scroll to bottom of messages whenever messages change
  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Focus input when chat opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // Extract name from user messages
  const extractName = (message: string): string | null => {
    // Simple name extraction patterns
    const namePatterns = [
      /my name is ([A-Z][a-z]+ ?[A-Z]?[a-z]*)/i,
      /I am ([A-Z][a-z]+ ?[A-Z]?[a-z]*)/i,
      /I'm ([A-Z][a-z]+ ?[A-Z]?[a-z]*)/i,
      /call me ([A-Z][a-z]+ ?[A-Z]?[a-z]*)/i
    ];
    
    for (const pattern of namePatterns) {
      const match = message.match(pattern);
      if (match && match[1]) {
        return match[1];
      }
    }
    return null;
  };

  // Determine the most appropriate response category
  const determineResponseCategory = (message: string): ResponseCategory => {
    const lowerMsg = message.toLowerCase();
    
    if (/hello|hi |hey|howdy|greetings|good (morning|afternoon|evening)/.test(lowerMsg)) {
      return 'greeting';
    }
    if (/product|bottle|jar|packaging|container|material|makhana|fridge/.test(lowerMsg)) {
      return 'product';
    }
    if (/price|cost|pricing|quote|expensive|cheap|discount|offer/.test(lowerMsg)) {
      return 'pricing';
    }
    if (/ship|deliver|shipping|delivery|timeframe|when|arrive|tracking/.test(lowerMsg)) {
      return 'shipping';
    }
    if (/pay|payment|credit card|upi|bank transfer|invoice|billing/.test(lowerMsg)) {
      return 'payment';
    }
    if (/return|refund|guarantee|warranty|unsatisfied|broken|damaged|quality/.test(lowerMsg)) {
      return 'returns';
    }
    
    return 'general';
  };

  // Format time for messages
  const formatTime = (date: Date) => {
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  // Get icon for response category
  const getCategoryIcon = (category?: ResponseCategory) => {
    if (!category) return <Info className="h-4 w-4" />;
    
    switch (category) {
      case 'product': return <Package className="h-4 w-4" />;
      case 'shipping': return <TruckIcon className="h-4 w-4" />;
      case 'payment': return <CreditCard className="h-4 w-4" />;
      case 'pricing': return <Info className="h-4 w-4" />;
      case 'returns': return <Check className="h-4 w-4" />;
      case 'greeting': return <Smile className="h-4 w-4" />;
      default: return <Info className="h-4 w-4" />;
    }
  };

  // Get category label for UI display
  const getCategoryLabel = (category?: ResponseCategory) => {
    if (!category) return "Information";
    
    switch (category) {
      case 'product': return "Products";
      case 'shipping': return "Shipping";
      case 'payment': return "Payment";
      case 'pricing': return "Pricing";
      case 'returns': return "Returns";
      case 'greeting': return "Greeting";
      default: return "Information";
    }
  };

  // Position classes based on the position prop
  const getPositionClasses = () => {
    switch (position) {
      case 'bottom-right': return 'bottom-4 sm:bottom-6 md:bottom-8 right-4 sm:right-6 md:right-8';
      case 'bottom-left': return 'bottom-4 sm:bottom-6 md:bottom-8 left-4 sm:left-6 md:left-8';
      case 'top-right': return 'top-4 sm:top-6 md:top-8 right-4 sm:right-6 md:right-8';
      case 'top-left': return 'top-4 sm:top-6 md:top-8 left-4 sm:left-6 md:left-8';
      default: return 'bottom-4 sm:bottom-6 md:bottom-8 right-4 sm:right-6 md:right-8';
    }
  };

  // Handle sending a message - either from text input or suggestion
  const handleSendMessage = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (inputValue.trim() === '') return;
    
    // Add user message
    const userMessage: Message = {
      id: Date.now().toString(),
      text: inputValue,
      sender: 'user',
      timestamp: new Date(),
    };
    
    setMessages(prev => [...prev, userMessage]);
    setInputValue('');
    
    // Check if message contains a name
    const extractedName = extractName(userMessage.text);
    if (extractedName && !userName) {
      setUserName(extractedName);
    }
    
    // Determine response category based on user message
    const category = determineResponseCategory(userMessage.text);
    
    // Simulate AI typing
    setIsTyping(true);
    
    // Simulate AI response after a delay
    setTimeout(() => {
      // Get responses for the determined category
      const categoryResponses = aiResponsesByCategory[category] || [];
      const randomResponse = categoryResponses.length > 0 
        ? categoryResponses[Math.floor(Math.random() * categoryResponses.length)]
        : "I understand. Let me help you with that.";
      
      const aiMessage: Message = {
        id: (Date.now() + 1).toString(),
        text: randomResponse,
        sender: 'ai',
        timestamp: new Date(),
        category: category
      };
      
      setMessages(prev => [...(prev || []), aiMessage]);
      setIsTyping(false);
      
      // Set quick reply options for the next interaction
      const followUps = followUpRepliesByCategory[category] || [];
      // Only show 2-3 random follow-up options
      const selectedFollowUps = followUps.length > 0
        ? followUps
            .sort(() => 0.5 - Math.random())
            .slice(0, Math.min(3, followUps.length))
        : ["Can I help with anything else?", "Tell me more"];
      
      setQuickReply(selectedFollowUps);
      
    }, 1500);
  };

  // Handle clicking a suggested question
  const handleSuggestedQuestion = (question: string) => {
    setInputValue(question);
    
    // Auto-submit after a short delay to simulate user typing
    setTimeout(() => {
      const formEvent = new Event('submit') as unknown as React.FormEvent;
      handleSendMessage(formEvent);
    }, 300);
  };

  return (
    <>
      {/* Floating Chat Button - Enhanced visibility and pop-out effect */}
      <div 
        className={`fixed ${getPositionClasses()} z-[10000]`}
        style={{ 
          position: 'fixed', 
          zIndex: 10000, 
          display: 'block', 
          opacity: 1, 
          visibility: 'visible',
          pointerEvents: 'auto',
          filter: 'drop-shadow(0 0 8px rgba(239, 68, 68, 0.5))',
          transform: 'translateZ(0)'
        }}
      >
        {/* Pulsing effect with higher contrast */}
        {!isOpen && showPulse && (
          <motion.div
            initial={{ scale: 0.8, opacity: 0.7 }}
            animate={{ scale: 1.8, opacity: 0 }}
            transition={{ duration: 1.5, repeat: 2, repeatType: "reverse" }}
            className="absolute inset-0 bg-red-500 dark:bg-red-400 rounded-full"
            style={{ zIndex: 9998 }}
          />
        )}
        
        <motion.button
          onClick={() => setIsOpen(prev => !prev)}
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.95 }}
          className={`relative p-3 sm:p-4 md:p-5 ${isOpen ? 'bg-red-600' : 'bg-red-500'} text-white rounded-full shadow-lg border-[3px] border-white dark:border-gray-200`}
          style={{ 
            opacity: 1, 
            display: 'block', 
            visibility: 'visible', 
            zIndex: 10000,
            outline: 'none',
            backgroundColor: isOpen ? '#dc2626' : '#ef4444',
            color: '#ffffff',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
          }}
          aria-label={isOpen ? "Close chat" : "Open chat"}
        >
          {isOpen ? (
            <X className="h-5 w-5 sm:h-6 sm:w-6 md:h-7 md:w-7 text-white" />
          ) : (
            <div className="relative">
              <MessageCircle className="h-5 w-5 sm:h-6 sm:w-6 md:h-7 md:w-7 text-white" />
              <motion.div
                initial={{ opacity: 1 }}
                animate={{ opacity: [1, 0.5, 1] }}
                transition={{ repeat: Infinity, duration: 2 }}
                className="absolute -top-2 -right-2 bg-yellow-300 rounded-full p-1"
                style={{ opacity: 1, visibility: 'visible', zIndex: 10001 }}
              >
                <Zap className="h-3 w-3 text-red-600" />
              </motion.div>
              <motion.span 
                className="absolute -bottom-1 -right-1 text-xs font-bold bg-white dark:bg-gray-800 text-red-600 dark:text-red-400 px-2 py-0.5 rounded-full shadow-sm"
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.5 }}
                style={{ visibility: 'visible', zIndex: 10001 }}
              >
                Chat
              </motion.span>
            </div>
          )}
        </motion.button>
      </div>

      {/* Chat Window with improved conversation flow */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            transition={{ duration: 0.2 }}
            className={`fixed ${position === 'bottom-right' || position === 'bottom-left' ? 'bottom-20 sm:bottom-24 md:bottom-28' : 'top-20 sm:top-24 md:top-28'} ${position.includes('right') ? 'right-4 sm:right-6 md:right-8' : 'left-4 sm:left-6 md:left-8'} w-[calc(100vw-2rem)] sm:w-[380px] md:w-96 bg-white dark:bg-gray-800 flex flex-col z-[9998] shadow-xl border-2 border-gray-200 dark:border-gray-700 rounded-lg`}
            style={{
              position: 'fixed',
              display: 'flex',
              visibility: 'visible',
              opacity: 1,
              willChange: 'transform, opacity',
              transform: 'translateZ(0)',
              backfaceVisibility: 'hidden',
              maxHeight: '75vh',
              minHeight: '350px',
              height: 'auto',
              zIndex: 9998,
              overflow: 'hidden'
            }}
          >
            {/* Chat Header */}
            <div 
              className="p-3 bg-red-600 flex justify-between items-center sticky top-0 z-10"
              style={{ 
                backgroundColor: '#dc2626',
                borderBottom: '1px solid rgba(0,0,0,0.1)',
                flexShrink: 0,
                minHeight: '50px'
              }}
            >
              <div className="flex items-center space-x-2">
                <div 
                  className="bg-white/20 p-1.5 rounded-full"
                  style={{ backgroundColor: 'rgba(255,255,255,0.2)' }}
                >
                  <Bot className="h-4 w-4 text-white" style={{ color: '#ffffff' }} />
                </div>
                <div>
                  <h3 className="font-medium text-sm text-white" style={{ color: '#ffffff' }}>{assistantName}</h3>
                  <div className="flex items-center text-xs text-white" style={{ color: '#ffffff' }}>
                    <span className="h-2 w-2 bg-green-400 rounded-full mr-2 animate-pulse" style={{ backgroundColor: '#4ade80' }}></span>
                    Online | Ready to help
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button 
                  onClick={() => setIsOpen(false)} 
                  className="text-white hover:text-gray-200 bg-white/20 rounded-full p-1.5 hover:bg-white/30 transition-colors"
                  style={{ 
                    backgroundColor: 'rgba(255,255,255,0.2)', 
                    color: '#ffffff',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    opacity: 1,
                    visibility: 'visible'
                  }}
                >
                  <X className="h-4 w-4 text-white" style={{ color: '#ffffff' }} />
                </button>
              </div>
            </div>

            {/* Main Chat Messages Area - Sequential conversation flow */}
            <div 
              className="flex-1 p-3 bg-white dark:bg-gray-900 overflow-y-auto"
              style={{ 
                backgroundColor: 'white',
                color: 'black',
                position: 'relative',
                minHeight: '200px',
                maxHeight: 'calc(75vh - 140px)',
                height: 'auto',
                overflowY: 'auto',
                overflowX: 'hidden',
                display: 'block',
                WebkitOverflowScrolling: 'touch',
                scrollbarWidth: 'thin',
                scrollbarColor: '#f87171 transparent',
                padding: '12px'
              }}
              data-dark-style={{
                backgroundColor: '#111827',
                color: 'white'
              }}
            >
              {/* Message bubbles - Bot and User messages displayed sequentially */}
              {(messagesToRender || []).map(message => (
                <div
                  key={message.id}
                  className={`mb-3 flex ${message.sender === 'user' ? 'justify-end' : 'justify-start'}`}
                  style={{
                    marginBottom: '12px', 
                    display: 'flex', 
                    justifyContent: message.sender === 'user' ? 'flex-end' : 'flex-start'
                  }}
                >
                  {message.sender === 'ai' && (
                    <div 
                      className="h-10 w-10 rounded-full bg-red-500 flex items-center justify-center flex-shrink-0 mr-2 shadow-md"
                      style={{ 
                        height: '40px', 
                        width: '40px',
                        borderRadius: '9999px',
                        backgroundColor: '#ef4444',
                        marginRight: '8px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      <Bot className="h-5 w-5 text-white" style={{ color: '#ffffff' }} />
                    </div>
                  )}
                  <div
                    className={`max-w-[80%] px-4 py-3 rounded-2xl ${
                      message.sender === 'user'
                        ? 'bg-red-500 text-white rounded-tr-none'
                        : 'bg-gray-100 dark:bg-gray-800 text-gray-800 dark:text-gray-100 rounded-tl-none'
                    }`}
                    style={{ 
                      maxWidth: '80%',
                      padding: '12px 16px',
                      borderRadius: '16px',
                      ...(message.sender === 'user' 
                        ? { 
                            backgroundColor: '#ef4444', 
                            color: 'white',
                            borderTopRightRadius: '0' 
                          } 
                        : { 
                            backgroundColor: '#f3f4f6', 
                            color: '#1f2937',
                            borderTopLeftRadius: '0' 
                          }
                      )
                    }}
                    data-dark-style={message.sender === 'ai' ? {
                      backgroundColor: '#1f2937',
                      color: '#f3f4f6'
                    } : undefined}
                  >
                    {message.category && message.sender === 'ai' && (
                      <div 
                        className="flex items-center mb-2 text-xs text-red-600 dark:text-red-400 font-medium"
                        style={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          marginBottom: '8px',
                          fontSize: '0.75rem',
                          color: '#dc2626',
                          fontWeight: '500'
                        }}
                        data-dark-style={{ color: '#f87171' }}
                      >
                        {getCategoryIcon(message.category)}
                        <span className="ml-1">{getCategoryLabel(message.category)}</span>
                      </div>
                    )}
                    <p className="text-sm" style={{ fontSize: '0.875rem' }}>{message.text}</p>
                    <p 
                      className={`text-xs mt-1 flex items-center ${message.sender === 'user' ? 'text-white/80' : 'text-gray-500 dark:text-gray-400'}`}
                      style={{ 
                        fontSize: '0.75rem',
                        marginTop: '4px',
                        display: 'flex',
                        alignItems: 'center',
                        color: message.sender === 'user' ? 'rgba(255,255,255,0.8)' : '#6b7280'
                      }}
                      data-dark-style={message.sender === 'ai' ? { color: '#9ca3af' } : undefined}
                    >
                      <Clock className="h-3 w-3 mr-1" style={{ height: '12px', width: '12px', marginRight: '4px' }} />
                      {formatTime(message.timestamp)}
                    </p>
                    
                    {/* Quick Suggested Replies after each AI message */}
                    {message.sender === 'ai' && message.id === messagesToRender[messagesToRender.length - 1].id && !isTyping && (
                      <div 
                        className="mt-2 flex flex-wrap gap-1"
                        style={{ 
                          marginTop: '8px',
                          display: 'flex', 
                          flexWrap: 'wrap', 
                          gap: '4px',
                          visibility: 'visible'
                        }}
                      >
                        {/* Show suggested questions only after the initial greeting */}
                        {message.category === 'greeting' ? (
                          questionsToShow.map((question, index) => (
                            <button
                              key={`q-${index}`}
                              onClick={() => handleSuggestedQuestion(question)}
                              className="text-xs bg-red-50 dark:bg-gray-700 text-red-800 dark:text-red-300 px-2 py-1 rounded-full hover:bg-red-100 dark:hover:bg-red-900/30 transition-colors duration-200 border border-red-100 dark:border-red-900/30"
                              style={{ 
                                fontSize: '0.7rem',
                                backgroundColor: '#fef2f2',
                                color: '#991b1b',
                                padding: '4px 8px',
                                borderRadius: '9999px',
                                border: '1px solid #fee2e2',
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                visibility: 'visible',
                                opacity: 1,
                                whiteSpace: 'nowrap',
                                marginBottom: '4px'
                              }}
                              data-dark-style={{
                                backgroundColor: '#374151',
                                color: '#fca5a5',
                                borderColor: 'rgba(127, 29, 29, 0.3)'
                              }}
                            >
                              {question}
                            </button>
                          ))
                        ) : (
                          // Show quick replies for follow-up messages
                          (quickReplies.length > 0 ? quickReplies : ["Can I help with anything else?", "Tell me more"]).map((reply, index) => (
                            <button
                              key={`r-${index}`}
                              onClick={() => handleSuggestedQuestion(reply)}
                              className="text-xs bg-red-50 dark:bg-gray-700 text-red-800 dark:text-red-300 px-2 py-1 rounded-full hover:bg-red-100 dark:hover:bg-red-900/30 transition-colors duration-200 border border-red-100 dark:border-red-900/30"
                              style={{ 
                                fontSize: '0.7rem',
                                backgroundColor: '#fef2f2',
                                color: '#991b1b',
                                padding: '4px 8px',
                                borderRadius: '9999px',
                                border: '1px solid #fee2e2',
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                visibility: 'visible',
                                opacity: 1,
                                whiteSpace: 'nowrap',
                                marginBottom: '4px'
                              }}
                              data-dark-style={{
                                backgroundColor: '#374151',
                                color: '#fca5a5',
                                borderColor: 'rgba(127, 29, 29, 0.3)'
                              }}
                            >
                              <Star className="h-2.5 w-2.5 mr-1" style={{ height: '10px', width: '10px', marginRight: '4px' }} />
                              {reply}
                            </button>
                          ))
                        )}
                      </div>
                    )}
                  </div>
                </div>
              ))}
              
              {/* AI Typing Indicator */}
              {isTyping && (
                <div 
                  className="mb-4 flex justify-start"
                  style={{ marginBottom: '16px', display: 'flex', justifyContent: 'flex-start' }}
                >
                  <div 
                    className="h-10 w-10 rounded-full bg-red-500 flex items-center justify-center flex-shrink-0 mr-2 shadow-md"
                    style={{ 
                      height: '40px', 
                      width: '40px',
                      borderRadius: '9999px',
                      backgroundColor: '#ef4444',
                      marginRight: '8px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    <Bot className="h-5 w-5 text-white animate-pulse" style={{ color: '#ffffff', height: '20px', width: '20px' }} />
                  </div>
                  <div
                    className="bg-gray-100 dark:bg-gray-800 text-gray-800 dark:text-gray-100 px-4 py-3 rounded-2xl rounded-tl-none max-w-[80%] shadow-sm"
                    style={{ 
                      backgroundColor: '#f3f4f6',
                      color: '#1f2937',
                      padding: '12px 16px',
                      borderRadius: '16px',
                      borderTopLeftRadius: '0',
                      maxWidth: '80%'
                    }}
                    data-dark-style={{
                      backgroundColor: '#1f2937',
                      color: '#f3f4f6'
                    }}
                  >
                    <div className="flex items-center space-x-2" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span className="flex space-x-1.5" style={{ display: 'flex', gap: '6px' }}>
                        <span 
                          className="h-2.5 w-2.5 bg-red-400 rounded-full animate-bounce" 
                          style={{ 
                            height: '10px', 
                            width: '10px', 
                            backgroundColor: '#f87171', 
                            borderRadius: '9999px',
                            animationDelay: '0ms'
                          }}
                        ></span>
                        <span 
                          className="h-2.5 w-2.5 bg-red-400 rounded-full animate-bounce" 
                          style={{ 
                            height: '10px', 
                            width: '10px', 
                            backgroundColor: '#f87171', 
                            borderRadius: '9999px',
                            animationDelay: '150ms'
                          }}
                        ></span>
                        <span 
                          className="h-2.5 w-2.5 bg-red-400 rounded-full animate-bounce" 
                          style={{ 
                            height: '10px', 
                            width: '10px', 
                            backgroundColor: '#f87171', 
                            borderRadius: '9999px',
                            animationDelay: '300ms'
                          }}
                        ></span>
                      </span>
                    </div>
                  </div>
                </div>
              )}
              
              <div ref={messagesEndRef} />
            </div>

            {/* Chat Input Area */}
            <form 
              onSubmit={handleSendMessage} 
              className="p-2 bg-white dark:bg-gray-800 border-t border-gray-100 dark:border-gray-700 flex items-center sticky bottom-0 z-10"
              style={{ 
                backgroundColor: 'white',
                borderTop: '1px solid #f3f4f6',
                padding: '8px',
                display: 'flex',
                alignItems: 'center',
                position: 'sticky',
                bottom: 0,
                zIndex: 10,
                flexShrink: 0,
                minHeight: '56px'
              }}
              data-dark-style={{
                backgroundColor: '#1f2937',
                borderTopColor: '#374151'
              }}
            >
              <input
                ref={inputRef}
                type="text"
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                placeholder="Type your message..."
                className="flex-1 bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-white border-0 rounded-l-xl py-2 px-3 text-sm focus:ring-1 focus:ring-red-400 dark:focus:ring-red-500 focus:outline-none placeholder-gray-500 dark:placeholder-gray-400"
                style={{ 
                  flexGrow: 1,
                  backgroundColor: '#f3f4f6',
                  color: '#1f2937',
                  border: 'none',
                  borderRadius: '0.75rem 0 0 0.75rem',
                  padding: '10px 12px',
                  fontSize: '0.875rem',
                  outline: 'none',
                  height: '40px'
                }}
                data-dark-style={{
                  backgroundColor: '#374151',
                  color: 'white',
                  placeholderColor: '#9ca3af'
                }}
              />
              <button
                type="submit"
                className="bg-red-500 hover:bg-red-600 text-white rounded-r-xl px-3 py-2 disabled:opacity-50 transition-all duration-200"
                disabled={inputValue.trim() === '' || isTyping}
                style={{ 
                  backgroundColor: '#ef4444',
                  color: 'white',
                  borderRadius: '0 0.75rem 0.75rem 0',
                  padding: '8px 12px',
                  transition: 'all 0.2s',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: '40px'
                }}
              >
                <Send className="h-5 w-5" style={{ height: '18px', width: '18px', color: 'white' }} />
              </button>
            </form>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default LiveChat; 